import { resetTaskClientFeedback, resetTaskClientFeedbackSummary } from './store/events';
import { TaskClientFeedbackFilterInput } from 'dy-frontend-http-repository/lib/modules/TaskClientFeedback/inputs';
import { useStore } from 'effector-react';
import React, { useEffect, useState } from 'react';
import { $client } from '../../store/states';
import { amountOfTaskClientFeedbacksOnPage } from './consts';
import { fetchTaskClientFeedback, fetchTaskClientFeedbackSummary } from './store/effects';
import { $taskClientFeedback, $taskClientFeedbackSummary } from './store/states';
import TaskClientFeedback from '@containers/components/TaskClientFeedback';
import Flex from '@app/components/Flex';
import { Intent, Spinner } from '@blueprintjs/core';
import Pagination from '@app/components/Pagination';
import { usePageTitle } from '@app/hooks';
import { Endpoints } from '@app/data/consts';
import { ToastUtils } from '@app/data/utils';
import { HTTPErrorType } from 'dy-frontend-http-repository/lib/data/enums';
import { HTTPErrorResponse } from 'dy-frontend-http-repository/lib/data/types';
import { useNavigate, useParams } from 'react-router-dom';

const ClientTaskFeedback: React.FC = () => {
    const { changeTitle } = usePageTitle('Client Task Feedback');
    const navigate = useNavigate();

    const client = useStore($client);
    const taskClientFeedback = useStore($taskClientFeedback);
    const taskClientFeedbackSummary = useStore($taskClientFeedbackSummary);
    const isFetchingTaskClientFeedback = useStore(fetchTaskClientFeedback.pending);

    const [page, setPage] = useState(1);

    const handleLoadPage = async (newPage: number) => {
        if (!client) return;

        // Get page offset
        const pageOffset = (newPage - 1) * amountOfTaskClientFeedbacksOnPage;

        // Get task client feedback filter
        const filter: TaskClientFeedbackFilterInput = {
            task_owner_user_id: [client.id],
        };

        try {
            // Fetch client feedback
            await fetchTaskClientFeedback({
                pagination: {
                    _pagination: { limit: amountOfTaskClientFeedbacksOnPage, offset: pageOffset },
                },
                filter,
            });

            // Fetch client summary
            await fetchTaskClientFeedbackSummary({ filter });
        } catch (e) {
            // Log
            console.error(e);

            const response = (e as any).response as HTTPErrorResponse;
            if (response.data.type === HTTPErrorType.MISSING) {
                // Show error message
                ToastUtils.showToast({
                    message: `Feedback for client with ID of ${client.id} was not found`,
                    intent: Intent.DANGER,
                });

                // Go to client tasks page
                navigate(Endpoints.CLIENT_TASKS.replace(':clientId', client.id));
            }
        }
    };

    useEffect(() => {
        handleLoadPage(page);

        // eslint-disable-next-line
    }, [page]);

    useEffect(() => {
        if (!client) {
            return;
        }

        changeTitle(`Client Task Feedback - ${client.first_name} ${client.last_name}`);
    }, [client?.first_name, client?.last_name]);

    useEffect(() => {
        return () => {
            resetTaskClientFeedback();
            resetTaskClientFeedbackSummary();
        };
    }, []);

    if (!client) {
        return null;
    }

    if (!taskClientFeedback || !taskClientFeedbackSummary) {
        return (
            <Flex direction="row" justify="center">
                <Spinner />
            </Flex>
        );
    }

    const renderPagination = () => {
        // Admins were not fetched yet
        if (taskClientFeedback === null) {
            return null;
        }

        // Check if offset paginator exist
        if (taskClientFeedback.paginator === null) {
            return;
        }

        // Only one page exist
        if (!taskClientFeedback.paginator.has_more && taskClientFeedback.paginator.offset === 0) {
            return null;
        }

        return (
            <Flex justify="flex-end">
                <Pagination
                    fetching={isFetchingTaskClientFeedback}
                    hasMore={taskClientFeedback.paginator.has_more}
                    className="mt-2"
                    page={page}
                    amountOfItemsOnPage={amountOfTaskClientFeedbacksOnPage}
                    totalItems={taskClientFeedback.paginator.total}
                    onPageChange={(newPage) => setPage(newPage)}
                />
            </Flex>
        );
    };

    return (
        <div>
            <TaskClientFeedback
                layout="list"
                loading={isFetchingTaskClientFeedback}
                taskClientFeedback={taskClientFeedback.items}
                taskClientFeedbackSummary={taskClientFeedbackSummary}
            />

            {/* Pagination */}
            {renderPagination()}
        </div>
    );
};

export default ClientTaskFeedback;
